import { useState } from 'react';
import type { LoginApp } from '../../identity/api';
import { Button, Dialog, TextField } from '@nyce/admin-kit/ui';
import { activeTargetInfo, client, useLogin } from '../../identity/session';
import {
  Code,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  TagsField,
  useAsync,
  useNotice,
} from '../../components/common';

const ID_RX = /^[a-z0-9][a-z0-9-]{0,62}$/;

function LoginAppForm({
  initial,
  onSave,
  onClose,
}: {
  initial: LoginApp | null;
  onSave: (id: string, name: string, redirectUris: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [id, setId] = useState(initial?.id ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [uris, setUris] = useState<string[]>(
    initial?.redirectUris ?? [`${window.location.origin}/login/callback`]
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await onSave(id.trim(), name.trim(), uris);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={initial ? `Login app ${initial.id}` : 'New login app'}
      maxWidth={600}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            loading={busy}
            disabled={!ID_RX.test(id.trim()) || !name.trim() || uris.length === 0}
          >
            Save
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Id (client_id)"
          value={id}
          onChange={(e) => setId(e.target.value)}
          disabled={!!initial}
          required
          autoFocus={!initial}
          hint="Lowercase slug, e.g. opshub; the hub's Auth:LoginAppId / AuthTargets:<n>:LoginAppId."
        />
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          required
        />
        <div className="nyk-span2">
          <TagsField
            label="Redirect URIs"
            value={uris}
            onChange={setUris}
            placeholder="https://operations.nycelogic.com/login/callback"
            hint="One per hub or app that signs in here (Enter adds another). Exact match, https only (http only for localhost). A hub's is <origin>/login/callback."
          />
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Browser apps allowed to sign users in through this AUTH's `/authorize` (federated sign-in, `docs/saml-entra.md`),
 * each with its exact redirect URIs. Global per AUTH environment, not per tenant.
 */
export function LoginAppsPage() {
  const login = useLogin();
  const canWrite = !!login?.access.canWrite;
  const apps = useAsync(() => client.loginApps.list(), []);
  const [editing, setEditing] = useState<{ value: LoginApp | null } | null>(null);
  const toast = useNotice();
  const save = async (id: string, name: string, redirectUris: string[]) => {
    await client.loginApps.put(id, { name, redirectUris });
    toast.success(`Saved ${id}`);
    await apps.reload();
  };
  const remove = async (app: LoginApp) => {
    try {
      await client.loginApps.remove(app.id);
      toast.success(`Removed ${app.id}`);
      await apps.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  return (
    <Page
      title="Login apps"
      description={`Browser apps that may sign users in through ${activeTargetInfo()?.label ?? 'this'} AUTH's single sign-on (authorization code + PKCE).`}
      actions={canWrite && <Button onClick={() => setEditing({ value: null })}>New login app</Button>}
    >
      <ErrorBox error={apps.error} />
      {toast.element}
      <Loading loading={apps.loading} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" aria-label="Login apps" data-testid="login-apps-table">
            <thead>
              <tr>
                <th>App</th>
                <th>Redirect URIs</th>
                <th>Id</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(apps.data ?? []).map((a) => (
                <tr key={a.id} data-login-app={a.id}>
                  <td className="nyk-card-title nyk-wrap">
                    <b>{a.name}</b>
                  </td>
                  <td data-label="Redirect URIs" className="nyk-wrap">
                    {a.redirectUris.map((u) => (
                      <div key={u}>{u}</div>
                    ))}
                  </td>
                  <td data-label="Id">
                    <Code>{a.id}</Code>
                  </td>
                  <td className="nyk-row-actions">
                    {canWrite && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing({ value: a })}>
                          Edit
                        </Button>
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          message={`Remove login app ${a.id}? Its sign-ins through single sign-on stop working.`}
                          onConfirm={() => void remove(a)}
                        >
                          Remove
                        </ConfirmButton>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {!apps.loading && !apps.data?.length && (
          <EmptyState
            title="No login apps."
            hint="Register the hub (id opshub, redirect <hub origin>/login/callback) before its operators can use single sign-on."
          />
        )}
      </div>
      {editing && <LoginAppForm initial={editing.value} onSave={save} onClose={() => setEditing(null)} />}
    </Page>
  );
}
